import { useState } from "react";
import {
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from "react-native";
import {
  NavigationProp,
  ParamListBase,
  RouteProp,
} from "@react-navigation/native";
import { FontAwesome, Entypo } from "@expo/vector-icons";
import { LineChart } from "react-native-chart-kit";

// Components
import Divider from "../components/common/Divider";
import Times, { TimeOption } from "../components/common/Times";

// Styles and utils
import { randomVisibilityData } from "../utils";
import { Colors, Spacing, Typography } from "../styles";

const profiles = [
  {
    image:
      "https://images.unsplash.com/photo-1597223557154-721c1cecc4b0?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=880&q=80",
    name: "Janette Paul",
    color: Colors.teal700,
    data: randomVisibilityData(),
  },
  {
    image:
      "https://images.unsplash.com/photo-1597223557154-721c1cecc4b0?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=880&q=80",
    name: "Samantha Jones",
    color: Colors.coral700,
    data: randomVisibilityData(),
  },
  {
    image:
      "https://images.unsplash.com/photo-1597223557154-721c1cecc4b0?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=880&q=80",
    name: "Jimmy Jones",
    color: Colors.yellow700,
    data: randomVisibilityData(),
  },
];

const ProfileIcon = ({
  image,
  name,
  color,
  isSelected,
  toggle,
  style,
}: {
  image: string;
  name: string;
  color: string;
  isSelected: boolean;
  toggle: () => void;
  style?: ViewStyle;
}) => {
  return (
    <TouchableWithoutFeedback onPress={toggle}>
      <View style={[styles.row, style, { opacity: isSelected ? 1 : 0.7 }]}>
        <View style={styles.profileInfo}>
          <View style={[styles.profileIcon, { borderColor: color }]}>
            <Image
              source={{
                uri: image,
              }}
              style={{ height: "100%", width: "100%" }}
              resizeMode="cover"
            />
          </View>
          <Text style={Typography.paragraph}>{name}</Text>
        </View>
        {isSelected && (
          <FontAwesome name="check" size={14} color={Colors.white} />
        )}
      </View>
    </TouchableWithoutFeedback>
  );
};

const Compare = ({
  navigation,
  route,
}: {
  navigation: NavigationProp<ReactNavigation.RootParamList>;
  route: RouteProp<ParamListBase, string>;
}) => {
  const [selected, setSelected] = useState<number[]>([0]);

  const [time, setTime] = useState<TimeOption>("max");

  const params = route.params as any;
  const title = params.title as string;

  const graphMargin = 15;
  const width = 250;

  return (
    <View style={styles.container}>
      <View style={[styles.row, styles.header]}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Entypo name="chevron-small-left" size={24} color={Colors.white} />
        </TouchableOpacity>

        <Text style={Typography.title}>Compare</Text>
        <View style={{ width: 24 }} />
      </View>

      <View style={[styles.card, { marginBottom: 20 }]}>
        {profiles.map((p, index) => (
          <>
            <ProfileIcon
              image={p.image}
              name={p.name}
              color={p.color}
              toggle={() => {
                let s = [...selected];
                if (selected.includes(index)) {
                  s = s.filter((i) => i !== index);
                } else {
                  s.push(index);
                }
                setSelected(s);
              }}
              isSelected={selected.includes(index)}
            />
            {index !== profiles.length - 1 && (
              <Divider style={{ marginVertical: 10 }} />
            )}
          </>
        ))}
      </View>

      {selected.length > 0 && (
        <View style={styles.card}>
          <Text style={[Typography.cardTitle, { marginBottom: 10 }]}>
            {title}
          </Text>
          <LineChart
            data={{
              labels: [],
              datasets: selected.map((i) => ({
                data: profiles[i].data,
                color: (opacity = 1) => profiles[i].color,
                strokeWidth: 2,
              })),
            }}
            width={width + graphMargin}
            height={120}
            segments={3}
            chartConfig={{
              backgroundColor: Colors.plum700,
              backgroundGradientFrom: Colors.plum700,
              backgroundGradientTo: Colors.plum700,

              //   fillShadowGradientFrom: graphColor,
              fillShadowGradientFromOpacity: 0,
              //   fillShadowGradientTo: "transparent",
              fillShadowGradientToOpacity: 0,
              //   fillShadowGradientToOffset: 0.8,
              decimalPlaces: 0, // optional, defaults to 2dp
              color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
              labelColor: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
              style: {
                borderRadius: 16,
              },
            }}
            withInnerLines={false}
            withDots={false}
            bezier
            style={{
              margin: 0,
              padding: 0,
              marginLeft: -graphMargin,
            }}
          />
          <Times selected={time} setSelected={(time) => setTime(time)} />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { padding: Spacing.page },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  header: {
    marginBottom: 20,
  },
  card: {
    borderRadius: Spacing.cardRadius,
    backgroundColor: Colors.plum700,
    padding: Spacing.card,
  },
  profileInfo: {
    flexDirection: "row",
    alignItems: "center",
  },
  profileIcon: {
    width: 30,
    height: 30,
    borderRadius: 30 / 2,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: Colors.white,
    marginRight: 6,
  },
});

export default Compare;
